import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { Save } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ViewModeSelector } from '../Pipelines/PipelineControls';

import { useTenant } from '../../../contexts/TenantContext';
import { OrderActionsProvider, useOrderActions } from '../../../contexts/OrderActionsContext';
import { usePersistedFilters } from '../../../hooks/usePersistedFilters';
import { PageHeader } from '../../../components/StandardPage';
import { Header } from '../../../components/SubHeaderFilter/types';
import ConfirmationModal from '../../../components/ModalConfirmation';
import { Content } from './style';
import { Button } from './AddProduct/style';
import stock from '../../../assets/stock/TableMovementHeader/list-active.svg'
import resource from '../../../assets/home/resource.svg'
import steel from '../../../assets/home/steel.svg'

const productFinalTableHeaders: Header[] = [
  { key: 'name', label: 'Produto', type: 'string' },
  { key: 'norm', label: 'Norma', type: 'string' },
  { key: 'scriptName', label: 'Script', type: 'string' },
  { key: 'items', label: 'Itens', type: 'string' },
  { key: 'deadline', label: 'Prazo (dias)', type: 'number' },
  { key: 'cost', label: 'Custo (últ.)', type: 'currency' },
  { key: 'price', label: 'Preço (últ)', type: 'currency' },
];

const getTabs = (isFashionTenant: boolean) => {
  if (isFashionTenant) {
    return [
      { path: '/panel/stock', image: stock, name: 'Tecidos' },
      { path: '/panel/stock/resource', image: resource, name: 'Aviamentos' },
      { path: '/panel/product-final', image: steel, name: 'Produto engenheirado' }
    ]
  }

  return [
    { path: '/panel/stock', image: stock, name: 'Matéria prima' },
    { path: '/panel/resource', image: resource, name: 'Insumos' },
    { path: '/panel/product-final', image: steel, name: 'Produto engenheirado' }
  ]
}

const getActiveIndex = (pathname: string) => {
  if (pathname.includes('/panel/resource')) return 1;
  if (pathname.includes('/panel/product-final')) return 2;
  if (pathname.includes('/panel/stock')) return 0;
  return 0;
};

const ProductFinalsLayoutContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerSaveOrder } = useOrderActions();
  const queryClient = useQueryClient();

  const [refresh, setRefresh] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [viewMode, setViewMode] = useState<'board' | 'table'>('table');

  const filtersState = usePersistedFilters('product_final');

  const isCreatePage = location.pathname.includes('/panel/product-final/create');
  const isEditPage = location.pathname.includes('/panel/product-final/edit');
  const isFormPage = isCreatePage || isEditPage;
  const newProductId = location.state?.newProductId;
  const { search } = useOutletContext<{ search: string }>();

  const tabIndexActived = getActiveIndex(location.pathname);

  const handleCancel = () => {
    setShowCancelModal(false);
    navigate('/panel/product-final');
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['products'] });
    setRefresh(prev => !prev);
  };

  const { isFashionTenant } = useTenant()
  const tabItems = getTabs(isFashionTenant)

  const headerTitle = isFormPage
    ? (isEditPage ? 'Editar Produto' : `Cadastrar produto engenheirado ${newProductId || ''}`)
    : 'Estoque';

  return (
    <>
      <PageHeader
        title={headerTitle}
        tabs={!isFormPage ? tabItems.map((tab, index) => ({ key: String(index), label: tab.name, icon: tab.image })) : undefined}
        activeTab={String(tabIndexActived)}
        onTabChange={(key) => {
          const index = Number(key)
          if (index === 0) {
            navigate('/panel/stock')
          } else if (index === 1) {
            navigate(isFashionTenant ? '/panel/stock/resource' : '/panel/resource')
          } else if (index === 2) {
            navigate('/panel/product-final')
          }
        }}
        onReload={!isFormPage ? handleRefresh : undefined}
        filterHeaders={!isFormPage ? productFinalTableHeaders : undefined}
        appliedFilters={!isFormPage ? filtersState.appliedFilters : undefined}
        onApplyFilter={!isFormPage ? filtersState.handleApplyFilter : undefined}
        onRemoveFilter={!isFormPage ? filtersState.handleRemoveFilter : undefined}
        onClearAllFilters={!isFormPage ? filtersState.handleClearAll : undefined}
        createButton={!isFormPage ? { label: 'Cadastrar produto', onClick: () => navigate('/panel/product-final/create') } : undefined}
        rightExtra={!isFormPage ? (
          <ViewModeSelector
            value={viewMode}
            onChange={setViewMode}
            boardLabel='Quadro'
            tableLabel='Tabela'
          />
        ) : undefined}
        extraActions={
          isFormPage ? (
            <>
              <Button variant='danger-outline' onClick={() => setShowCancelModal(true)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={triggerSaveOrder}>
                <Save size={18} style={{ marginRight: '8px' }} />
                {isEditPage ? 'Salvar Alterações' : 'Salvar Produto'}
              </Button>
            </>
          ) : undefined
        }
      />

      <Content>
        <Outlet context={{ refresh, appliedFilters: filtersState.appliedFilters, search, viewMode }} />
      </Content>

      {showCancelModal && (
        <ConfirmationModal
          isOpen={showCancelModal}
          title={isEditPage ? "Cancelar Edição de Produto" : "Cancelar Criação de Produto"}
          message="Você tem certeza? Todas as alterações não salvas serão perdidas."
          actions={[
            { label: 'Confirmar', onClick: handleCancel, variant: 'danger' },
            { label: 'Voltar', onClick: () => setShowCancelModal(false), variant: 'outline' }
          ]}
        />
      )}
    </>
  );
};

const ProductFinals: React.FC = () => {
  return (
    <OrderActionsProvider>
      <ProductFinalsLayoutContent />
    </OrderActionsProvider>
  );
};

export default ProductFinals;
